import moment from 'moment'

import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { toBaseFareClass } from 'constants/flight'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { getFlightSearchKey, getFlightSearchLuxPlusCacheKey } from 'lib/flights/flightUtils'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import { hasSelectedRoomsOrDefault } from 'lib/offer/offerPageUtil'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { isRoomsInvalid } from 'lib/search/searchUtils'

interface PrefetchFlightSearchParamsOptions {
  offer: App.HotelOffer | App.BedbankOffer
  originAirportCode: string | undefined
  checkInDate: OfferPageState['checkInDate']
  checkOutDate: OfferPageState['checkOutDate']
  duration: OfferPageState['duration']
  fareClass: OfferPageState['fareClass']
  rooms: OfferPageState['rooms']
  currency: string
  requestBundledLuxPlusPricing: boolean
}

interface PrefetchFlightSearchKeyParams {
  originAirportCode: string | undefined
  destinationAirportCode: string | undefined
  departDate: string | undefined
  returnDate: string | undefined
  occupancies: Array<App.Occupants>
  fareClass: string
  currency: string
  source?: string
  minReturningDepartureTime?: string
  maxArrivalTime?: string
}

export function getPrefetchFlightDestinationAirportCode(offer: App.HotelOffer | App.BedbankOffer) {
  return isBedbankOffer(offer) ? offer.flights?.airportCode : offer.flightDestinationPort
}

export function getPrefetchFlightReturnDate(checkInDate: string | undefined, checkOutDate: string | undefined, duration: number) {
  if (checkOutDate) return checkOutDate
  if (checkInDate && duration) return moment(checkInDate).add(duration, 'days').format(ISO_DATE_FORMAT)
  return undefined
}

export function isOfferBundledWithFlightsOnly(offer: App.HotelOffer | App.BedbankOffer) {
  return !isBedbankOffer(offer) && offer.bundledWithFlightsOnly
}

export function getPrefetchFlightsFromOfferSearchParams(options: PrefetchFlightSearchParamsOptions) {
  const {
    offer,
    originAirportCode,
    checkInDate,
    checkOutDate,
    duration,
    fareClass,
    rooms,
    currency,
    requestBundledLuxPlusPricing,
  } = options
  const destinationAirportCode = getPrefetchFlightDestinationAirportCode(offer)
  const returnDate = getPrefetchFlightReturnDate(checkInDate, checkOutDate, duration)

  if (
    !originAirportCode ||
    !destinationAirportCode ||
    originAirportCode === destinationAirportCode ||
    !checkInDate ||
    !returnDate ||
    !hasSelectedRoomsOrDefault(rooms) ||
    isRoomsInvalid(rooms)
  ) {
    return undefined
  }

  const itinerary = [
    {
      departureAirport: originAirportCode,
      arrivalAirport: destinationAirportCode,
      departureDate: checkInDate,
    },
    {
      departureAirport: destinationAirportCode,
      arrivalAirport: originAirportCode,
      departureDate: returnDate,
    },
  ]
  const occupants = countOccupantsForFlights(rooms)

  return {
    itinerary,
    currency,
    occupancies: [occupants],
    fareClass: toBaseFareClass(fareClass),
    minReturningDepartureTime: isBedbankOffer(offer) ? undefined : offer.flightsMinReturningDepartureTime,
    maxArrivalTime: isBedbankOffer(offer) ? undefined : offer.flightsMaxArrivalTime,
    source: offer.type,
    accommodationCheckInDate: checkInDate,
    key: getFlightSearchLuxPlusCacheKey(undefined, requestBundledLuxPlusPricing),
    forceBundleId: isBedbankOffer(offer) ? undefined : offer.forceBundleId,
  }
}

export function getPrefetchFlightsFromOfferSearchKey(options: PrefetchFlightSearchParamsOptions) {
  const params = getPrefetchFlightsFromOfferSearchParams(options)
  return params ? getFlightSearchKey(params) : undefined
}

export function getPrefetchFlightsFromOfferSearchKeys(options: Omit<PrefetchFlightSearchParamsOptions, 'requestBundledLuxPlusPricing'>) {
  return [
    getPrefetchFlightsFromOfferSearchKey({
      ...options,
      requestBundledLuxPlusPricing: false,
    }),
    getPrefetchFlightsFromOfferSearchKey({
      ...options,
      requestBundledLuxPlusPricing: true,
    }),
  ].filter((key): key is string => !!key)
}

export function getPrefetchFlightsSearchKeysFromParams(params: PrefetchFlightSearchKeyParams) {
  const {
    originAirportCode,
    destinationAirportCode,
    departDate,
    returnDate,
    occupancies,
    fareClass,
    currency,
    source,
    minReturningDepartureTime,
    maxArrivalTime,
  } = params

  if (!originAirportCode || !destinationAirportCode || originAirportCode === destinationAirportCode || !departDate || !returnDate) {
    return []
  }

  const itinerary = [
    {
      departureAirport: originAirportCode,
      arrivalAirport: destinationAirportCode,
      departureDate: departDate,
    },
    {
      departureAirport: destinationAirportCode,
      arrivalAirport: originAirportCode,
      departureDate: returnDate,
    },
  ]

  return [
    getFlightSearchKey({
      itinerary,
      currency,
      occupancies,
      fareClass,
      minReturningDepartureTime,
      maxArrivalTime,
      source,
      key: getFlightSearchLuxPlusCacheKey(undefined, false),
    }),
    getFlightSearchKey({
      itinerary,
      currency,
      occupancies,
      fareClass,
      minReturningDepartureTime,
      maxArrivalTime,
      source,
      key: getFlightSearchLuxPlusCacheKey(undefined, true),
    }),
  ]
}
